import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ICheckpoint } from '../../../models/IEvent';
import { IReaderAdmin } from '../../../models/IReader';
import ReaderServiceInstance from '../../../services/ReaderService';
import DelayEditor from '../../common/DelayEditor';
import IEventBaseDataProps from './IEventBaseDataProps';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            minWidth: 280,
        },
        root: {
            width: '100%',
        },
        paper: {
            width: '100%',
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
        },
        delayRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginTop: theme.spacing(2),
        },
    }));

interface IEventReadersData {
    checkpoints: ICheckpoint[];
    readers: string[];
}
export default function EventReaders(props: IEventBaseDataProps<IEventReadersData>) {
    const classes = useStyles();
    const { t } = useTranslation("common");
    const data = props.data;

    const [readerDetails, setReaderDetails] = useState<IReaderAdmin[]>([]);
    const [delayValue, setDelayValue] = useState<number | ''>('');
    const [updatingDelay, setUpdatingDelay] = useState(false);
    const [suggestingDelay, setSuggestingDelay] = useState(false);

    const selectedReaderId = data.checkpoints.length > 0 ? data.checkpoints[0].readerId : '';

    useEffect(() => {
        if (data.readers.length === 0) return;
        ReaderServiceInstance.getReaders().then(allReaders => {
            const relevant = allReaders.filter(r => data.readers.includes(r.readerId));
            setReaderDetails(relevant);
        }).catch(e => console.error(e));
    }, [data.readers]);

    useEffect(() => {
        if (!selectedReaderId) {
            setDelayValue('');
            return;
        }
        const reader = readerDetails.find(r => r.readerId === selectedReaderId);
        setDelayValue(reader?.estimatedDelayMs ?? '');
    }, [selectedReaderId, readerDetails]);

    const triggerDataChange = (newData: IEventReadersData) => {
        if (props.onDataChange) {
            props.onDataChange(newData);
        }
    }

    const handleUpdateDelay = async () => {
        if (!selectedReaderId || delayValue === '') return;
        setUpdatingDelay(true);
        try {
            await ReaderServiceInstance.updateReaderDelay(selectedReaderId, delayValue as number);
            setReaderDetails(prev =>
                prev.map(r => r.readerId === selectedReaderId ? { ...r, estimatedDelayMs: delayValue as number } : r)
            );
        } catch (e) {
            console.error(e);
        } finally {
            setUpdatingDelay(false);
        }
    }

    const handleSuggestDelay = async () => {
        if (!selectedReaderId) return;
        setSuggestingDelay(true);
        try {
            const suggested = await ReaderServiceInstance.getSuggestedDelay(selectedReaderId);
            setDelayValue(suggested);
        } catch (e) {
            console.error(e);
        } finally {
            setSuggestingDelay(false);
        }
    }

    return (<Fragment>
        <Paper className={classes.paper}>
            <TextField
                id="checkpoints"
                select
                label={t("eventsetup.checkpoints")}
                value={selectedReaderId}
                onChange={e => {
                    if (e.target.value.length > 0) {
                        triggerDataChange({
                            ...data,
                            checkpoints: [{
                                readerId: e.target.value
                            }]
                        });
                    }
                }}
                helperText={t("eventsetup.checkpointshelper")}
            >
                {data.readers.map((reader) => (
                    <MenuItem
                        key={reader}
                        value={reader}>
                        {reader}
                    </MenuItem>
                ))}
            </TextField>
            {selectedReaderId && (
                <Box className={classes.delayRow}>
                    <DelayEditor
                        valueMs={delayValue !== '' ? delayValue as number : 0}
                        onChange={ms => setDelayValue(ms)}
                        label={t("eventsetup.readerDelay") as string}
                    />
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUpdateDelay}
                            disabled={delayValue === '' || updatingDelay}
                            startIcon={updatingDelay ? <CircularProgress size={16} /> : undefined}
                        >
                            {t("eventsetup.updateDelay")}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleSuggestDelay}
                            disabled={suggestingDelay}
                            startIcon={suggestingDelay ? <CircularProgress size={16} /> : undefined}
                        >
                            {t("eventsetup.suggestDelay")}
                        </Button>
                    </Box>
                </Box>
            )}
            {selectedReaderId && (() => {
                const reader = readerDetails.find(r => r.readerId === selectedReaderId);
                if (!reader) return null;
                return (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        {reader.manufacturer} {reader.model}{reader.description ? ` - ${reader.description}` : ''}
                    </Typography>
                );
            })()}
        </Paper>
    </Fragment>);
}